'use client';

/**
 * Marketplace course tile — a 1:1 visual match of the akademi.ws
 * `/catalog` card: a type-badge chip on the image, a participant +
 * thumbs-up/down meta row (or a start/end-date pair for a live course),
 * "Ücretsiz" or a price (with an optional struck-through was-price), a
 * divider, and a footer with the provider name + logo and an add-to-cart
 * icon. No hover popover — the reference card doesn't have one.
 */
import { useTranslation } from 'react-i18next';
import { PortalLink } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import {
  Users,
  ThumbsUp,
  ThumbsDown,
  Globe,
  Home,
  Layers,
  ShoppingCart,
  Plus,
  SignalHigh,
} from 'lucide-react';
import { CourseCoverImage } from '../_shared/CourseCoverImage';
import { CourseProgressBar } from '../_shared/CourseProgressBar';
import { cardReset } from '../_shared/styles';
import type { CourseCardProps } from './CourseCard.types';
import type { CardGridSpec } from './gridSpecs';
import type { ProgressTextPlace } from './registry';

const MARKETPLACE_CARD_CSS = `
.lms-marketplace-card-anchor {
  transition: box-shadow 200ms ease, transform 200ms ease;
}
.lms-marketplace-card-anchor:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.10), 0 2px 4px rgba(0,0,0,0.06);
  transform: translateY(-2px);
}
.lms-marketplace-card-anchor:hover .lms-mp-cart { color: var(--color-brand-600, #287f96); }
.lms-marketplace-card-anchor:focus-visible {
  outline: 2px solid var(--color-brand-600, #287f96);
  outline-offset: 2px;
}
`;

/** Pick the chip label + glyph. A live session → "Sınıf içi" (Home); an
 *  explicit `badge`/`kicker` wins; otherwise "E-Öğrenme" (Globe). */
function resolveKind(c: CourseCardProps, t: ReturnType<typeof useTranslation>['t']) {
  const explicit = c.badge?.label ?? c.kicker;
  if (explicit) {
    const low = explicit.toLocaleLowerCase();
    const Icon = /program/.test(low) ? Layers : /sınıf|class|yüz|person/.test(low) ? Home : Globe;
    return { label: explicit, Icon };
  }
  if (c.liveStartDate) return { label: t('card.marketplace.classroom'), Icon: Home };
  return { label: t('card.marketplace.elearning'), Icon: Globe };
}

function formatDate(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function MarketplaceCourseCard({
  course: c,
  progressBar: {
    show: showProgress = false,
    showText: showProgressText = false,
    textPlace: progressTextPlace = 'outside-right',
  } = {},
}: {
  course: CourseCardProps;
  showRating?: boolean;
  progressBar?: { show?: boolean; showText?: boolean; textPlace?: ProgressTextPlace };
}) {
  const { t, i18n } = useTranslation('site-builder-blocks');
  const locale = i18n.language || 'tr';
  const kind = resolveKind(c, t);
  const provider = c.trainers || c.instructor;
  const isLive = Boolean(c.liveStartDate);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: MARKETPLACE_CARD_CSS }} />
      <Card asChild>
        <PortalLink
          href={c.href}
          className="lms-marketplace-card-anchor"
          style={{
            ...cardReset,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: 'var(--color-surface-card, #fff)',
            border: '1px solid var(--color-border-default, #e2e8f0)',
            borderRadius: 'var(--radius-lg, 12px)',
            overflow: 'hidden',
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          {/* Image + type chip */}
          <div
            style={{
              aspectRatio: 'var(--app-course-image-aspect-ratio, 16/10)',
              position: 'relative',
              overflow: 'hidden',
              background: 'var(--color-surface-muted, #eef2f6)',
            }}
          >
            <CourseCoverImage
              image={typeof c.image === 'string' ? c.image : undefined}
              resizeTo="480x0"
            />
            <span
              style={{
                position: 'absolute',
                left: 10,
                bottom: 10,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '4px 10px',
                borderRadius: 'var(--radius-full, 999px)',
                background: 'rgba(255,255,255,0.92)',
                color: 'var(--color-brand-600, #287f96)',
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: 600,
                boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
              }}
            >
              <kind.Icon size={13} />
              {kind.label}
            </span>
          </div>

          {/* Body */}
          <div
            style={{
              flex: 1,
              padding: 'var(--space-4, 16px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3, 12px)',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 'var(--font-size-md, 16px)',
                fontWeight: 700,
                lineHeight: 1.3,
                color: 'var(--color-text-primary, #1c1d1f)',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {c.title}
            </h3>

            {/* Meta: participants + thumbs */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-4, 16px)',
                fontSize: 'var(--font-size-sm, 13px)',
                color: 'var(--color-text-tertiary, #6a6f73)',
              }}
            >
              {typeof c.enrolled === 'number' && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Users size={15} />
                  {c.enrolled.toLocaleString(locale)}
                </span>
              )}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <ThumbsUp size={15} />
                {(c.likeCount ?? 0).toLocaleString(locale)}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <ThumbsDown size={15} />
                {(c.dislikeCount ?? 0).toLocaleString(locale)}
              </span>
              {c.level && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    marginLeft: 'auto',
                  }}
                >
                  <SignalHigh size={15} />
                  {c.level}
                </span>
              )}
            </div>

            {/* Live date range */}
            {isLive && (
              <div style={{ display: 'flex', gap: 'var(--space-5, 24px)' }}>
                {[
                  { label: t('card.marketplace.startDate'), value: c.liveStartDate },
                  { label: t('card.marketplace.endDate'), value: c.liveEndDate },
                ].map(
                  (col) =>
                    col.value && (
                      <div key={col.label}>
                        <div
                          style={{
                            fontSize: 'var(--font-size-xs, 12px)',
                            color: 'var(--color-text-tertiary, #6a6f73)',
                          }}
                        >
                          {col.label}
                        </div>
                        <div
                          style={{
                            fontSize: 'var(--font-size-sm, 13px)',
                            fontWeight: 600,
                            color: 'var(--color-text-primary, #1c1d1f)',
                          }}
                        >
                          {formatDate(col.value, locale)}
                        </div>
                      </div>
                    ),
                )}
              </div>
            )}

            {/* Price */}
            <div
              style={{
                marginTop: 'auto',
                display: 'flex',
                alignItems: 'baseline',
                gap: 'var(--space-2, 8px)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-lg, 18px)',
                  fontWeight: 700,
                  color: 'var(--color-text-primary, #1c1d1f)',
                }}
              >
                {c.price || t('card.marketplace.free')}
              </span>
              {c.price && c.listPrice && c.listPrice !== c.price && (
                <span
                  style={{
                    fontSize: 'var(--font-size-sm, 14px)',
                    color: 'var(--color-text-tertiary, #9ca3af)',
                    textDecoration: 'line-through',
                  }}
                >
                  {c.listPrice}
                </span>
              )}
            </div>

            {showProgress && typeof c.progress === 'number' && c.progress > 0 && (
              <CourseProgressBar
                progress={c.progress}
                showText={showProgressText}
                textPlace={progressTextPlace}
              />
            )}
          </div>

          {/* Footer: provider + add-to-cart */}
          <div
            style={{
              borderTop: '1px solid var(--color-border-subtle, #eef2f6)',
              padding: 'var(--space-3, 12px) var(--space-4, 16px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-2, 8px)',
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                minWidth: 0,
                fontSize: 'var(--font-size-sm, 13px)',
                fontWeight: 500,
                color: 'var(--color-text-secondary, #52525b)',
              }}
            >
              {provider &&
                (c.instructorAvatar ? (
                  <img
                    src={c.instructorAvatar}
                    alt=""
                    aria-hidden
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 6,
                      flexShrink: 0,
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <span
                    aria-hidden
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 6,
                      flexShrink: 0,
                      background:
                        'linear-gradient(135deg, var(--color-brand-500, #319db3), var(--color-brand-700, #1f6377))',
                    }}
                  />
                ))}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {provider || ''}
              </span>
            </span>
            {/* Live/classroom sessions have no cart affordance — they're
             *  registered for, not bought off the shelf. */}
            {!isLive && (
              <span
                className="lms-mp-cart"
                aria-hidden
                style={{
                  position: 'relative',
                  display: 'inline-flex',
                  flexShrink: 0,
                  color: 'var(--color-text-tertiary, #6a6f73)',
                }}
              >
                <ShoppingCart size={18} />
                <Plus
                  size={10}
                  strokeWidth={3}
                  style={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    background: 'var(--color-surface-card, #fff)',
                    borderRadius: '50%',
                  }}
                />
              </span>
            )}
          </div>
        </PortalLink>
      </Card>
    </>
  );
}

MarketplaceCourseCard.gridSpec = {
  kind: 'grid',
  minCardWidth: 260,
  gap: 'clamp(16px, 2vw, 20px)',
} satisfies CardGridSpec;
